import { Link } from "@/components/ui/link"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  PaymentLinkGet,
  PaymentLinkType,
  usePaymentLinks,
  usePaymentLinksActions,
} from "@/api/payment-links"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import ButtonCopy from "@/components/ui/button-copy"
import Loader from "@/components/layout/loader"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import { toast } from "sonner"
import { getShortUrl } from "@/api/payment-pages"
import { TokenIcon } from "@/components/token"

const TABLE_COLUMNS = 6

interface PaymentLinksTableProps {
  onLinkTransaction: (paymentLink: PaymentLinkGet) => void;
}

export default function PaymentLinksTable({
  onLinkTransaction,
}: PaymentLinksTableProps) {
  const { paymentLinks, hasMore, size, setSize } = usePaymentLinks()
  const { update } = usePaymentLinksActions()

  if (!paymentLinks) {
    return <Loader message="Loading payment links..." />
  }

  const onActivateDeactivate = async (paymentLink: PaymentLinkGet) => {
    await update(paymentLink.id, { active: !paymentLink.active })
    toast.success(
      `Payment link ${paymentLink.active ? "deactivated" : "activated"}`,
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="rounded-t-md">
          <TableHead className="pl-6">URL</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Created on</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {paymentLinks.map((paymentLink) => (
          <TableRow key={paymentLink.id}>
            <TableCell className="pl-6">
              <div className="font-medium flex items-center gap-4">
                <ButtonCopy
                  className="text-gray-300"
                  value={getShortUrl(paymentLink.paymentPage.shortId)}
                />
                <Link
                  to={`/payment-links/${paymentLink.id}`}
                  className="hidden sm:block"
                >
                  {paymentLink.paymentPage.shortId}
                </Link>
              </div>
            </TableCell>
            <TableCell>{paymentLink.title}</TableCell>
            <TableCell>
              {format(new Date(paymentLink.createdAt), "MMM do, yyyy")}
            </TableCell>
            <TableCell>
              <div className="flex flex-row items-center gap-2">
                <TokenIcon name={paymentLink.token} />
                <span>
                  {paymentLink.type === PaymentLinkType.CUSTOM_AMOUNT
                    ? `${paymentLink.minAmount || 0} - ${paymentLink.maxAmount || "∞"}`
                    : paymentLink.amount?.toLocaleString()}
                </span>
                <span>{paymentLink.token}</span>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={paymentLink.active ? "active" : "inactive"}>
                {paymentLink.active ? "Active" : "Inactive"}
              </Badge>
            </TableCell>
            <TableCell>
              <OptionsDropdownMenu>
                <DropdownMenuItem
                  onClick={() => onActivateDeactivate(paymentLink)}
                >
                  {paymentLink.active ? "Deactivate" : "Activate"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onLinkTransaction(paymentLink)}
                  disabled={!paymentLink.active}
                >
                  Link Transaction
                </DropdownMenuItem>
              </OptionsDropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {paymentLinks.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={TABLE_COLUMNS}
              className="text-center text-gray-300"
            >
              No payment links found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasMore && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={TABLE_COLUMNS}>
              <button
                onClick={() => setSize(size + 1)}
                className="flex flex-col w-full items-center bg-white p-3 text-center"
              >
                Load more...
              </button>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  )
}
